import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type OfferV11 } from '@redteclab/api/clients/bully'
import { IconInfo } from '@redteclab/icons'

import { useGetExperiment } from '../../../../../experiments/hooks'
import { EXPERIMENT_NAME_MARKETPLACE_HARMONISATION } from '../../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../../../experiments/model/EXPERIMENT_VARIATION'
import { useOpenableControls } from '../../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../../global-config'
import { productAccessorGetSellerPromotionFromOffer } from '../../../../model/productAccessor/productAccessorGetSellerPromotionFromOffer'
import { productAccessorOfferHasFreeItemSellerPromotion } from '../../../../model/productAccessor/productAccessorOfferHasFreeItemSellerPromotion'
import { ProductPageSellerPromotionModal } from '../../../../ProductPageSellerPromotionModal/ProductPageSellerPromotionModal'
import { useProductPageContext } from '../../../productPageContext'

type ProductOfferCardSellerPromotionLabelProps = {
  offer: OfferV11
}

export const ProductOfferCardSellerPromotionLabel: FC<
  ProductOfferCardSellerPromotionLabelProps
> = ({ offer }) => {
  const sellerPromotion = productAccessorGetSellerPromotionFromOffer(offer)
  const { superScripts } = useGlobalConfigContext()
  const { product } = useProductPageContext()
  const { handleClose, handleOpen, isOpen } = useOpenableControls()
  const isMarketplaceHarmonisationEnabled =
    useGetExperiment(EXPERIMENT_NAME_MARKETPLACE_HARMONISATION).variant ===
    EXPERIMENT_VARIATION.V1

  if (!sellerPromotion) {
    return null
  }

  const badgeAndDialogTitle = productAccessorOfferHasFreeItemSellerPromotion(
    offer,
  ) ? (
    <FormattedMessage
      id="seller.promotion.product.badge.freeItems"
      values={{
        payableItemCount:
          sellerPromotion.conditionValue - sellerPromotion.discountValue,
        sup: superScripts.sellerPromotions ? (
          <sup>{superScripts.sellerPromotions}</sup>
        ) : null,
        targetItemCount: sellerPromotion.conditionValue,
      }}
    />
  ) : (
    <FormattedMessage id="seller.promotion.product.badge" />
  )

  return (
    <>
      <button
        className={clsx(
          'flex items-center rounded-full px-2.5 py-1 text-xs',
          isMarketplaceHarmonisationEnabled
            ? 'bg-light-tertiary'
            : 'bg-light-brand-medium',
        )}
        data-qa-id="product-label-seller-promotion"
        onClick={() => {
          if (isMarketplaceHarmonisationEnabled) {
            handleOpen()
          }
        }}
        type="button"
      >
        {badgeAndDialogTitle}
        {isMarketplaceHarmonisationEnabled ? (
          <IconInfo className="ml-1 size-4 shrink-0" />
        ) : null}
      </button>
      {isMarketplaceHarmonisationEnabled ? (
        <ProductPageSellerPromotionModal
          dialogTitle={badgeAndDialogTitle}
          isOpen={isOpen}
          offer={offer}
          onClose={handleClose}
          product={product}
        />
      ) : null}
    </>
  )
}
